import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCookie, setCookie, removeCookie } from "../../utils/cookieUtils";
import { setSession, fetchUserProfile } from "./sessionSlice";
import apiClient from "../../services/apiClient";

const getSavedCallStatus = () => {
  try {
    const raw = getCookie("callStatus");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getSavedProductRemarks = () => {
  try {
    const raw = getCookie("productRemarks");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Async Thunk to fetch company settings (including leadStatuses / callStatus) from DB
 */
export const fetchCompanyCallStatus = createAsyncThunk("executor/fetchCompanyCallStatus", async (companyCodeParam, { getState, rejectWithValue }) => {
  try {
    const code = companyCodeParam || getState().executor?.companyInfo?.companyCode || getCookie("companyCode") || "";
    if (!code) return { companyCode: "" };

    const data = await apiClient.get(`/api/auth/company/${code}/settings`, {
      headers: { "x-active-company-code": code },
    });

    if (data?.success && data?.settings) {
      return {
        companyCode: code,
        leadStatuses: data.settings.leadStatuses || [],
        companyName: data.settings.companyName,
        companyAddress: data.settings.companyAddress,
        productRemarks: data.settings.productRemarks || [],
      };
    }
    return rejectWithValue(data?.message || "Failed to fetch company settings from DB");
  } catch (err) {
    return rejectWithValue(err.message || "Network error fetching call status from DB");
  }
});

const initialState = {
  companyInfo: {
    companyCode: getCookie("companyCode") || null,
    companyName: getCookie("companyName") || null,
    companyAddress: getCookie("companyAddress") || "",
    callStatus: getSavedCallStatus(),
    productRemarks: getSavedProductRemarks(),
  },
  loading: false,
  error: null,
};

export const executorSlice = createSlice({
  name: "executor",
  initialState,
  reducers: {
    setCompanyInfo: (state, action) => {
      const payload = action.payload || {};
      const resolvedCompanyCode = payload.companyCode || payload.code || state.companyInfo.companyCode;
      const resolvedCompanyName = payload.companyName || payload.name || state.companyInfo.companyName;
      const resolvedCompanyAddress = payload.companyAddress || state.companyInfo.companyAddress;

      const rawStatus = payload.callStatus || payload.leadStatuses;
      let resolvedCallStatus = state.companyInfo.callStatus;
      if (Array.isArray(rawStatus) && rawStatus.length > 0) {
        resolvedCallStatus = rawStatus;
      }

      let resolvedProductRemarks = state.companyInfo.productRemarks;
      if (Array.isArray(payload.productRemarks)) {
        resolvedProductRemarks = payload.productRemarks;
      }

      if (resolvedCompanyCode) setCookie("companyCode", resolvedCompanyCode, 7);
      if (resolvedCompanyName) setCookie("companyName", resolvedCompanyName, 7);
      if (resolvedCompanyAddress) setCookie("companyAddress", resolvedCompanyAddress, 7);
      if (resolvedCallStatus && resolvedCallStatus.length > 0) {
        setCookie("callStatus", JSON.stringify(resolvedCallStatus), 7);
      }
      if (resolvedProductRemarks && resolvedProductRemarks.length > 0) {
        setCookie("productRemarks", JSON.stringify(resolvedProductRemarks), 7);
      }

      state.companyInfo = {
        ...state.companyInfo,
        companyCode: resolvedCompanyCode,
        companyName: resolvedCompanyName,
        companyAddress: resolvedCompanyAddress,
        callStatus: resolvedCallStatus,
        productRemarks: resolvedProductRemarks,
      };
    },
    setCallStatus: (state, action) => {
      const statusList = action.payload;
      if (Array.isArray(statusList)) {
        setCookie("callStatus", JSON.stringify(statusList), 7);
        state.companyInfo.callStatus = statusList;
      }
    },
    clearCompanyInfo: (state) => {
      removeCookie("companyCode");
      removeCookie("companyName");
      removeCookie("companyAddress");
      removeCookie("callStatus");
      removeCookie("productRemarks");

      state.companyInfo = {
        companyCode: null,
        companyName: null,
        companyAddress: "",
        callStatus: [],
      };
    },
    setExecutorLoading: (state, action) => {
      state.loading = action.payload;
    },
    setExecutorError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setSession, (state, action) => {
        const user = action.payload?.userInfo || action.payload?.user;
        const code = user?.companyCode || action.payload?.companyCode;
        const name = user?.companyName || action.payload?.companyName;
        const addr = user?.companyAddress || action.payload?.companyAddress;
        const statuses = user?.leadStatuses || user?.callStatus;

        if (code) {
          state.companyInfo.companyCode = code;
          setCookie("companyCode", code, 7);
        }
        if (name) {
          state.companyInfo.companyName = name;
          setCookie("companyName", name, 7);
        }
        if (addr) {
          state.companyInfo.companyAddress = addr;
        }
        if (Array.isArray(statuses) && statuses.length > 0) {
          state.companyInfo.callStatus = statuses;
          setCookie("callStatus", JSON.stringify(statuses), 7);
        }
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        if (action.payload) {
          const { companyCode, companyName, companyAddress } = action.payload;
          if (companyCode) {
            state.companyInfo.companyCode = companyCode;
          }
          if (companyName) {
            state.companyInfo.companyName = companyName;
          }
          if (companyAddress) {
            state.companyInfo.companyAddress = companyAddress;
          }
        }
      })
      .addCase(fetchCompanyCallStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyCallStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.companyCode && !state.companyInfo.companyCode) {
          state.companyInfo.companyCode = action.payload.companyCode;
          setCookie("companyCode", action.payload.companyCode, 7);
        }
        if (action.payload?.leadStatuses && action.payload.leadStatuses.length > 0) {
          state.companyInfo.callStatus = action.payload.leadStatuses;
          setCookie("callStatus", JSON.stringify(action.payload.leadStatuses), 7);
        }
        if (action.payload?.companyName && !state.companyInfo.companyName) {
          state.companyInfo.companyName = action.payload.companyName;
          setCookie("companyName", action.payload.companyName, 7);
        }
        if (action.payload?.companyAddress && !state.companyInfo.companyAddress) {
          state.companyInfo.companyAddress = action.payload.companyAddress;
        }
        if (action.payload?.productRemarks && action.payload.productRemarks.length > 0) {
          state.companyInfo.productRemarks = action.payload.productRemarks;
          setCookie("productRemarks", JSON.stringify(action.payload.productRemarks), 7);
        }
      })
      .addCase(fetchCompanyCallStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setCompanyInfo, setCallStatus, clearCompanyInfo, setExecutorLoading, setExecutorError } = executorSlice.actions;

export default executorSlice.reducer;
